import { useEffect, useRef, useState } from 'react';

import { Box, Button, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

type CollapsedStyleProps = {
  lineClamp?: number;
};

const collapsedStyle = ({ lineClamp = 2 }: CollapsedStyleProps) => {
  return {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxOrient: 'vertical',
    lineClamp,
  };
};

const expandedStyle = {
  display: 'block',
  overflow: 'visible',
  textOverflow: 'unset',
  lineClamp: 'unset',
};

type ExpandableTextBoxProps = {
  lines?: number;
};

export const ExpandableTextBox: React.FC<React.PropsWithChildren<ExpandableTextBoxProps>> = ({
  children,
  lines = 2,
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const element = textRef.current;

    if (!element) return;

    setIsExpanded(false);

    element.className = css(expandedStyle);
    const fullHeight = element.scrollHeight;

    element.className = css(collapsedStyle({ lineClamp: lines }));
    const collapsedHeight = element.offsetHeight;

    setIsTruncated(fullHeight > collapsedHeight);
  }, [children, lines]);

  const defaultCss = isExpanded ? expandedStyle : collapsedStyle({ lineClamp: lines });

  return (
    <Box>
      <Text ref={textRef} css={defaultCss}>
        {children}
      </Text>

      {isTruncated && !isExpanded && (
        <Box css={{ mt: 'space.200' }}>
          <Button
            aria-expanded={isExpanded}
            variant="textPrimary"
            onClick={e => {
              setIsExpanded(true);
            }}
          >
            See more
          </Button>
        </Box>
      )}
    </Box>
  );
};
